import { Component, Inject, ChangeDetectionStrategy, Input, Output, EventEmitter, ViewChild, Renderer2, ViewEncapsulation, NgZone, NgModule } from '@angular/core';
import { DOCUMENT, CommonModule } from '@angular/common';
import { of } from 'rxjs/observable/of';
import { fromEvent } from 'rxjs/observable/fromEvent';
import { empty } from 'rxjs/observable/empty';
import { takeWhile, expand, delay } from 'rxjs/operators';

var ScrollbarComponent = (function () {
    function ScrollbarComponent(zone, renderer, document) {
        this.zone = zone;
        this.renderer = renderer;
        this.document = document;
        this._thumbSizeY = 0;
        this._thumbSizeX = 0;
        this._trackTopMax = 0;
        this._trackLeftMax = 0;
        this._scrollLeftMax = 0;
        this._scrollTopMax = 0;
        this._naturalThumbSizeY = 0;
        this._naturalThumbSizeX = 0;
        this._prevPageY = 0;
        this._prevPageX = 0;
        this._currXPos = 0;
        this._currYPos = 0;
        this._minThumbSize = 20;
        this.autoUpdate = true;
        this.autoHide = false;
        this.trackX = false;
        this.trackY = true;
        this.scrollState = new EventEmitter();
    }
    ScrollbarComponent.prototype.ngAfterViewInit = function () {
        var _this = this;
        this.zone.runOutsideAngular(function () {
            _this.barX = _this.barXRef.nativeElement;
            _this.barY = _this.barYRef.nativeElement;
            _this.thumbX = _this.thumbXRef.nativeElement;
            _this.thumbY = _this.thumbYRef.nativeElement;
            _this.view = _this.viewRef.nativeElement;
            _this.hideNativeScrollbars();
            _this.scrollWorker(null);
            _this._scrollSub$ = fromEvent(_this.view, 'scroll', function (e) { return _this.scrollWorker(e); }).subscribe();
            if (_this.trackX) {
                _this._barXSub$ = fromEvent(_this.barX, 'mousedown', function (e) { return _this.barXWorker(e); }).subscribe();
                _this._thumbXSub$ = fromEvent(_this.thumbX, 'mousedown', function (e) { return _this.thumbXWorker(e); }).subscribe();
            }
            if (_this.trackY) {
                _this._barYSub$ = fromEvent(_this.barY, 'mousedown', function (e) { return _this.barYWorker(e); }).subscribe();
                _this._thumbYSub$ = fromEvent(_this.thumbY, 'mousedown', function (e) { return _this.thumbYWorker(e); }).subscribe();
            }
            if (_this.autoUpdate) {
                _this._observer = new MutationObserver(function () { return _this.update(); });
                _this._observer.observe(_this.view, { subtree: true, childList: true });
            }
        });
    };
    ScrollbarComponent.prototype.ngOnDestroy = function () {
        if (this._scrollSub$) {
            this._scrollSub$.unsubscribe();
        }
        if (this.trackX) {
            this._barXSub$.unsubscribe();
            this._thumbXSub$.unsubscribe();
        }
        if (this.trackY) {
            this._barYSub$.unsubscribe();
            this._thumbYSub$.unsubscribe();
        }
        if (this._observer) {
            this._observer.disconnect();
        }
    };
    ScrollbarComponent.prototype.scrollXTo = function (to, duration) {
        var _this = this;
        if (duration === void 0) { duration = 200; }
        this.zone.runOutsideAngular(function () {
            of(duration).pipe(takeWhile(function () { return duration > 0; }), expand(function (d) {
                if (d > 0) {
                    var difference = to - _this.view.scrollLeft;
                    var perTick = difference / d * 10;
                    _this.renderer.setProperty(_this.view, 'scrollLeft', _this.view.scrollLeft + perTick);
                    return of(d - 10).pipe(delay(10));
                }
                else {
                    duration = d;
                    return empty();
                }
            })).subscribe();
        });
    };
    ScrollbarComponent.prototype.scrollYTo = function (to, duration) {
        var _this = this;
        if (duration === void 0) { duration = 200; }
        this.zone.runOutsideAngular(function () {
            of(duration).pipe(takeWhile(function () { return duration > 0; }), expand(function (d) {
                if (d > 0) {
                    var difference = to - _this.view.scrollTop;
                    var perTick = difference / d * 10;
                    _this.renderer.setProperty(_this.view, 'scrollTop', _this.view.scrollTop + perTick);
                    return of(d - 10).pipe(delay(10));
                }
                else {
                    duration = d;
                    return empty();
                }
            })).subscribe();
        });
    };
    ScrollbarComponent.prototype.update = function () {
        this.setThumbXPosition(this._currXPos, this.calculateThumbXSize());
        this.setThumbYPosition(this._currYPos, this.calculateThumbYSize());
    };
    ScrollbarComponent.prototype.scrollWorker = function (e) {
        this._thumbSizeX = this.thumbX.clientWidth;
        this._thumbSizeY = this.thumbY.clientHeight;
        this._trackLeftMax = this.barX.clientWidth - this._thumbSizeX;
        this._trackTopMax = this.barY.clientHeight - this._thumbSizeY;
        var thumbXPosition = this.view.scrollLeft * this._trackLeftMax / this._scrollLeftMax;
        var thumbYPosition = this.view.scrollTop * this._trackTopMax / this._scrollTopMax;
        this.setThumbXPosition(thumbXPosition, this.calculateThumbXSize());
        this.setThumbYPosition(thumbYPosition, this.calculateThumbYSize());
        this.scrollState.emit(e);
    };
    ScrollbarComponent.prototype.barXWorker = function (e) {
        if (e.target === e.currentTarget) {
            var offset = e.offsetX - this._naturalThumbSizeX * .5;
            var thumbPositionPercentage = offset * 100 / this.barX.clientWidth;
            var scrollLeft = thumbPositionPercentage * this.view.scrollWidth / 100;
            this.renderer.setProperty(this.view, 'scrollLeft', scrollLeft);
        }
    };
    ScrollbarComponent.prototype.barYWorker = function (e) {
        if (e.target === e.currentTarget) {
            var offset = e.offsetY - this._naturalThumbSizeY * .5;
            var thumbPositionPercentage = offset * 100 / this.barY.clientHeight;
            var scrollTop = thumbPositionPercentage * this.view.scrollHeight / 100;
            this.renderer.setProperty(this.view, 'scrollTop', scrollTop);
        }
    };
    ScrollbarComponent.prototype.thumbXWorker = function (e) {
        var _this = this;
        var startDrag = function (event) {
            _this._prevPageX = _this._thumbSizeX - e.offsetX;
            var offset = event.clientX - _this.barX.getBoundingClientRect().left;
            var thumbClickPosition = _this._thumbSizeX - _this._prevPageX;
            var scrollLeft = _this._scrollLeftMax * (offset - thumbClickPosition) / _this._trackLeftMax;
            _this.renderer.setProperty(_this.view, 'scrollLeft', scrollLeft);
        };
        var endDrag = function () {
            _this.document.onselectstart = null;
            mouseMoveSub$.unsubscribe();
            mouseUpSub$.unsubscribe();
            _this._prevPageX = 0;
        };
        this.document.onselectstart = function () { return false; };
        var mouseMoveSub$ = fromEvent(this.document.body, 'mousemove', startDrag).subscribe();
        var mouseUpSub$ = fromEvent(this.document.body, 'mouseup', endDrag).subscribe();
    };
    ScrollbarComponent.prototype.thumbYWorker = function (e) {
        var _this = this;
        var startDrag = function (event) {
            _this._prevPageY = _this._thumbSizeY - e.offsetY;
            var offset = event.clientY - _this.barY.getBoundingClientRect().top;
            var thumbClickPosition = _this._thumbSizeY - _this._prevPageY;
            var scrollTop = _this._scrollTopMax * (offset - thumbClickPosition) / _this._trackTopMax;
            _this.renderer.setProperty(_this.view, 'scrollTop', scrollTop);
        };
        var endDrag = function () {
            _this.document.onselectstart = null;
            mouseMoveSub$.unsubscribe();
            mouseUpSub$.unsubscribe();
            _this._prevPageY = 0;
        };
        this.document.onselectstart = function () { return false; };
        var mouseMoveSub$ = fromEvent(this.document.body, 'mousemove', startDrag).subscribe();
        var mouseUpSub$ = fromEvent(this.document.body, 'mouseup', endDrag).subscribe();
    };
    ScrollbarComponent.prototype.calculateThumbXSize = function () {
        this._naturalThumbSizeX = this.barX.clientWidth / this.view.scrollWidth * this.barX.clientWidth;
        this._scrollLeftMax = this.view.scrollWidth - this.view.clientWidth;
        return this.scrollBoundaries(this._naturalThumbSizeX, this._scrollLeftMax);
    };
    ScrollbarComponent.prototype.calculateThumbYSize = function () {
        this._naturalThumbSizeY = this.barY.clientHeight / this.view.scrollHeight * this.barY.clientHeight;
        this._scrollTopMax = this.view.scrollHeight - this.view.clientHeight;
        return this.scrollBoundaries(this._naturalThumbSizeY, this._scrollTopMax);
    };
    ScrollbarComponent.prototype.scrollBoundaries = function (naturalThumbSize, scrollMax) {
        return (naturalThumbSize < this._minThumbSize) ? this._minThumbSize : scrollMax ? naturalThumbSize : 0;
    };
    ScrollbarComponent.prototype.setThumbXPosition = function (x, width) {
        var transform = "translate3d(" + x + "px, 0, 0)";
        this.renderer.setStyle(this.thumbX, 'webkitTransform', transform);
        this.renderer.setStyle(this.thumbX, 'transform', transform);
        this.renderer.setStyle(this.thumbX, 'width', width + 'px');
        this._currXPos = x;
    };
    ScrollbarComponent.prototype.setThumbYPosition = function (y, height) {
        var transform = "translate3d(0, " + y + "px, 0)";
        this.renderer.setStyle(this.thumbY, 'webkitTransform', transform);
        this.renderer.setStyle(this.thumbY, 'transform', transform);
        this.renderer.setStyle(this.thumbY, 'height', height + 'px');
        this._currYPos = y;
    };
    ScrollbarComponent.prototype.hideNativeScrollbars = function () {
        var size = "calc(100% + " + this.getNativeScrollbarWidth() + "px)";
        this.renderer.setStyle(this.view, 'width', size);
        this.renderer.setStyle(this.view, 'height', size);
    };
    ScrollbarComponent.prototype.getNativeScrollbarWidth = function () {
        var element = this.document.createElement('div');
        element.style.position = 'absolute';
        element.style.top = '-9999px';
        element.style.width = '100px';
        element.style.height = '100px';
        element.style.overflow = 'scroll';
        element.style.msOverflowStyle = 'scrollbar';
        this.document.body.appendChild(element);
        var sw = element.offsetWidth - element.clientWidth;
        this.document.body.removeChild(element);
        return sw;
    };
    return ScrollbarComponent;
}());
ScrollbarComponent.decorators = [
    { type: Component, args: [{
                selector: 'ng-scrollbar',
                template: "<div class=\"ng-scrollbar-container\" [class.ng-scrollbar-autohide]=\"autoHide\">\n  <div #barX class=\"ng-scrollbar -horizontal {{barClass}}\" [class.ng-scrollbar-disabled]=\"!trackX\">\n    <div #thumbX class=\"ng-scrollbar-thumb {{thumbClass}}\"></div>\n  </div>\n  <div #barY class=\"ng-scrollbar -vertical {{barClass}}\" [class.ng-scrollbar-disabled]=\"!trackY\">\n    <div #thumbY class=\"ng-scrollbar-thumb {{thumbClass}}\"></div>\n  </div>\n  <div #view class=\"ng-scrollbar-view {{viewClass}}\">\n    <ng-content></ng-content>\n  </div>\n</div>\n",
                styles: ["ng-scrollbar{display:block;position:relative;height:100%;overflow:hidden!important}.ng-scrollbar-container{position:relative;overflow:hidden!important;width:100%;height:100%}.ng-scrollbar-autohide>.ng-scrollbar{opacity:0;-webkit-transition:opacity 120ms ease-out;transition:opacity 120ms ease-out}.ng-scrollbar-autohide:active>.ng-scrollbar,.ng-scrollbar-autohide:focus>.ng-scrollbar,.ng-scrollbar-autohide:hover>.ng-scrollbar{opacity:1;-webkit-transition:opacity 340ms ease-out;transition:opacity 340ms ease-out}.ng-scrollbar{position:absolute;right:2px;bottom:2px;z-index:1;border-radius:3px}.-vertical{width:6px;top:2px}.-vertical .ng-scrollbar-thumb{width:100%}.-horizontal{height:6px;left:2px}.-horizontal .ng-scrollbar-thumb{height:100%}.ng-scrollbar-disabled{display:none}.ng-scrollbar-thumb{position:relative;display:block;width:0;height:0;border-radius:inherit;background-color:rgba(0,0,0,.2);-webkit-transform:translate3d(0,0,0);transform:translate3d(0,0,0)}.ng-scrollbar-thumb:active,.ng-scrollbar-thumb:hover{background-color:rgba(0,0,0,.3)}.ng-scrollbar-view{width:100%;height:100%;overflow:scroll;-webkit-transform:translate3d(0,0,0);transform:translate3d(0,0,0);-webkit-overflow-scrolling:touch}"],
                changeDetection: ChangeDetectionStrategy.OnPush,
                encapsulation: ViewEncapsulation.None,
                preserveWhitespaces: false
            },] },
];
ScrollbarComponent.ctorParameters = function () { return [
    { type: NgZone, },
    { type: Renderer2, },
    { type: undefined, decorators: [{ type: Inject, args: [DOCUMENT,] },] },
]; };
ScrollbarComponent.propDecorators = {
    "barXRef": [{ type: ViewChild, args: ['barX',] },],
    "barYRef": [{ type: ViewChild, args: ['barY',] },],
    "thumbXRef": [{ type: ViewChild, args: ['thumbX',] },],
    "thumbYRef": [{ type: ViewChild, args: ['thumbY',] },],
    "viewRef": [{ type: ViewChild, args: ['view',] },],
    "autoUpdate": [{ type: Input },],
    "autoHide": [{ type: Input },],
    "trackX": [{ type: Input },],
    "trackY": [{ type: Input },],
    "viewClass": [{ type: Input },],
    "barClass": [{ type: Input },],
    "thumbClass": [{ type: Input },],
    "scrollState": [{ type: Output },],
};
var ScrollbarModule = (function () {
    function ScrollbarModule() {
    }
    return ScrollbarModule;
}());
ScrollbarModule.decorators = [
    { type: NgModule, args: [{
                imports: [CommonModule],
                declarations: [ScrollbarComponent],
                exports: [ScrollbarComponent]
            },] },
];
ScrollbarModule.ctorParameters = function () { return []; };

export { ScrollbarModule, ScrollbarComponent };
//# sourceMappingURL=ngx-scrollbar.js.map
